/* eslint-disable no-console */
import { BackgroundLayerSpecification, FilterSpecification, Map } from "@iventis/mapbox-gl";
import bbox from "@turf/bbox";
import throttle from "lodash.throttle";
import { Polygon } from "geojson";
import { createWorldPolygonWithCutOut } from "../../../utilities/geojson-helpers";
import { removeSourceOnceMapIsIdle } from "../engine-mapbox-helpers";

type Operations = {
    getLayerIds: () => string[];
};

/** Filters features within a polygon */
export class MapboxPolygonFilter {
    private readonly polygonFilter: Polygon;

    private readonly map: Map;

    private readonly operations: Operations;

    private readonly delayBetweenFitlerMapFeaturesWithinPolygonFilter = 750;

    private removed = false;

    private fitlerMapFeaturesWithinPolygonFilter: () => void;

    /** Key - layer id, Value - Array of feature ids which we want to filter  */
    private layerIdToFeaturesToFilter: Record<string, number[]> = {};

    private backgroundLayerId: string;

    constructor(polygonFilter: Polygon, map: Map, operations: Operations) {
        this.polygonFilter = polygonFilter;
        this.map = map;
        this.operations = operations;

        // When the map moves we need to check if we need to filter any features from the given layers are within the polygon filter
        this.fitlerMapFeaturesWithinPolygonFilter = throttle(this.filterMapFeaturesWithinPolygon.bind(this), this.delayBetweenFitlerMapFeaturesWithinPolygonFilter);
        this.map.on("move", this.fitlerMapFeaturesWithinPolygonFilter);
    }

    /** Replaces a background layer, with a fill layer with a cutout of the polygon filter */
    public replaceBackgroundLayer(layer: BackgroundLayerSpecification, beforeId: string) {
        // Ensure that the background layer has not already been added
        if (!this.map.getLayer(layer.id) && !this.map.getSource(layer.id)) {
            this.map.addSource(layer.id, { type: "geojson", data: createWorldPolygonWithCutOut(this.polygonFilter) });
            this.map.addLayer(
                {
                    id: layer.id,
                    type: "fill",
                    paint: {
                        "fill-color": layer.paint["background-color"] ?? "white",
                    },
                    source: layer.id,
                },
                beforeId
            );
        }
        this.backgroundLayerId = layer.id;
    }

    public filterMapFeaturesWithinPolygon() {
        if (this.removed) {
            return;
        }

        // Create bounding box around polygon filter
        const polygonFilterBbox = bbox(this.polygonFilter);

        const projectedSouthWest = this.map.project([polygonFilterBbox[0], polygonFilterBbox[1]]);
        const projectedNorthEast = this.map.project([polygonFilterBbox[2], polygonFilterBbox[3]]);

        // Query within the bounds to find what features are within the polygon filter
        const featuresOnScreen = this.map.queryRenderedFeatures([projectedSouthWest, projectedNorthEast], {
            layers: this.operations.getLayerIds().filter((layerId) => layerId !== this.backgroundLayerId),
        });

        let updated = false;

        // For each feature found in queryRenderedFeatures
        for (const feature of featuresOnScreen) {
            if (feature.layer == null) {
                console.warn(`Feature ${feature.id} does not have a layer`);
                continue;
            }

            if (feature.id == null) {
                console.warn(`Layer ${feature.layer.id} has a feature which does not have an id`);
                continue;
            }

            // Add it to all the other features we are filtering
            if (this.layerIdToFeaturesToFilter[feature.layer.id] == null) {
                this.layerIdToFeaturesToFilter[feature.layer.id] = [];
                this.layerIdToFeaturesToFilter[feature.layer.id].push(feature.id as number);
                updated = true;
            } else if (!this.layerIdToFeaturesToFilter[feature.layer.id].some((featureId) => featureId === feature.id)) {
                this.layerIdToFeaturesToFilter[feature.layer.id].push(feature.id as number);
                updated = true;
            }
        }

        // If a new feature has been added to the features we are filtering
        if (updated) {
            for (const [layerId, featureIds] of Object.entries(this.layerIdToFeaturesToFilter)) {
                const layer = this.map.getLayer(layerId);

                if (layer == null) {
                    console.warn(`Can't find layer of ${layerId}`);
                    continue;
                }

                // Create a filter expression to filter by feature id
                const layerFilterFeatureIds: FilterSpecification = ["!", ["in", ["id"], ["literal", featureIds]]];

                // Search for existing filter
                const existingFeatureFilter: FilterSpecification = layer.filter?.find((filter) => filter?.[0] === "!" && filter[1]?.[0] === "in");
                if (existingFeatureFilter) {
                    layer.filter.pop();
                    // Update the existing filter with the new one
                    const updatedFilter = [...layer.filter, layerFilterFeatureIds];
                    this.map.setFilter(layerId, updatedFilter);
                    continue;
                }

                // Update the map the given layer's filter
                switch (layer.filter?.[0]) {
                    case "all":
                        this.map.setFilter(layerId, [...layer.filter, layerFilterFeatureIds]);
                        break;
                    case "<=":
                    case "==":
                    case "!in":
                    case "match":
                    case "in":
                        this.map.setFilter(layerId, ["all", layer.filter, layerFilterFeatureIds]);
                        break;
                    case undefined:
                        this.map.setFilter(layerId, ["all", layerFilterFeatureIds]);
                        break;
                    default:
                        console.warn(`Layer filter of "${layer.filter?.[0]}" not handled`);
                }
            }
        }
    }

    public remove() {
        this.removed = true;
        this.map.off("move", this.fitlerMapFeaturesWithinPolygonFilter);
        if (this.map.getLayer(this.backgroundLayerId)) {
            this.map.removeLayer(this.backgroundLayerId);
        }
        if (this.map.getSource(this.backgroundLayerId)) {
            removeSourceOnceMapIsIdle(this.map, this.backgroundLayerId);
        }
    }
}
